import { Classes, TextArea } from "@blueprintjs/core";
import classnames from "classnames";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import React, { useMemo, useState } from "react";
import {
    CONFLICT_KIND_LABELS,
    formatDay,
    formatRange,
    openConflicts,
    PlanConflict,
    PlanConflictKind,
} from "../models/plan-conflicts";
import { ProjectPlanProject } from "../models/project-plan-types";
import styles from "./ConflictsPanel.module.scss";

interface ConflictsPanelProps {
    conflicts: PlanConflict[];
    /** Проекты графика — для номеров и ГИПа */
    projects: ProjectPlanProject[];
    /** Конфликт, к которому перешли кликом */
    selectedId: string | null;
    /** Может ли пользователь пометить конфликт допустимым (и снять пометку) */
    canMark: (conflict: PlanConflict) => boolean;
    /** Клик по конфликту: первый — прокрутка к нему, повторный — карточка */
    onSelect: (conflict: PlanConflict) => void;
    onMark: (conflict: PlanConflict, comment: string) => Promise<boolean>;
    onUnmark: (conflict: PlanConflict) => Promise<boolean>;
    onClose: () => void;
}

const KINDS: PlanConflictKind[] = ["overload", "absence", "deadline", "link", "dayoff"];

/** Цвет вида — полоска у карточек и маркер в заголовке группы */
const KIND_CLASSES: { [kind in PlanConflictKind]: string } = {
    overload: styles.kindOverload,
    absence: styles.kindAbsence,
    deadline: styles.kindDeadline,
    link: styles.kindLink,
    dayoff: styles.kindDayoff,
};

/** Что значит каждый вид — подсказка у заголовка группы */
const KIND_NOTES: { [kind in PlanConflictKind]: string } = {
    overload: "Два проекта в одном дне сотрудника",
    absence: "Работа стоит на отпуске, больничном или БС",
    deadline: "Работа позже планового срока проекта, срока по договору или срока раздела",
    link: "Расположение полос противоречит типу связи и лагу",
    dayoff: "Работа стоит на выходном или празднике — на графике этих дней нет",
};

/**
 * Панель «Конфликты» справа от графика: непогашенные конфликты по видам и отдельной
 * группой — помеченные допустимыми. Клик по конфликту прокручивает к нему график,
 * повторный клик открывает карточку. Перегрузку и выход за срок можно пометить
 * допустимыми — только с комментарием, он уходит в историю проекта.
 */
export const ConflictsPanel: React.FC<ConflictsPanelProps> = (props) => {
    const { conflicts, projects, selectedId, canMark, onSelect, onMark, onUnmark, onClose } = props;

    const orders = useMemo(() => new Map(projects.map((project) => [project.id, project])), [projects]);

    const open = useMemo(() => openConflicts(conflicts), [conflicts]);
    const marked = useMemo(() => conflicts.filter((conflict) => !!conflict.mark), [conflicts]);

    const [showMarked, setShowMarked] = useState(false);

    /** Конфликт, для которого открыт ввод комментария */
    const [editing, setEditing] = useState<string | null>(null);
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);

    const startMark = (conflict: PlanConflict) => {
        setEditing(conflict.id);
        setComment("");
    };

    const submitMark = async (conflict: PlanConflict) => {
        if (!comment.trim()) {
            return;
        }

        setBusy(true);

        try {
            if (await onMark(conflict, comment.trim())) {
                setEditing(null);
            }
        } finally {
            setBusy(false);
        }
    };

    const unmark = async (conflict: PlanConflict) => {
        setBusy(true);

        try {
            await onUnmark(conflict);
        } finally {
            setBusy(false);
        }
    };

    const renderItem = (conflict: PlanConflict) => {
        const projectsOf = conflict.orderIds.map((id) => orders.get(id)).filter(Boolean) as ProjectPlanProject[];
        const gips = Array.from(new Set(projectsOf.map((project) => project.employer).filter(Boolean)));
        const section =
            conflict.contentGuid && projectsOf[0]
                ? projectsOf[0].sections.find((item) => item.id === conflict.contentGuid)
                : undefined;

        const details = [
            conflict.link
                ? `${conflict.link.caption} от ${conflict.link.from}: ${
                      conflict.link.edge === "start" ? "начать" : "закончить"
                  } не раньше ${formatDay(conflict.link.required)}`
                : "",
            conflict.deadline ? `срок ${formatDay(conflict.deadline.day)}` : "",
            // отсутствие целиком, а не только дни конфликта: видно, докуда сотрудника нет
            conflict.absencePeriod
                ? `${conflict.absenceType || "Отсутствие"} ${formatRange(
                      conflict.absencePeriod.from,
                      conflict.absencePeriod.to
                  )}`
                : conflict.absenceType,
            conflict.kind === "absence"
                ? `работа на ${conflict.daysCount} раб. дн.`
                : `${conflict.daysCount} раб. дн.`,
        ].filter(Boolean);

        return (
            <div
                key={conflict.id}
                className={classnames(styles.item, KIND_CLASSES[conflict.kind], {
                    [styles.selected]: conflict.id === selectedId,
                    [styles.itemMarked]: !!conflict.mark,
                })}
                onClick={() => onSelect(conflict)}
            >
                {/* подсказка — только у заголовка: у всей карточки она всплывала поверх формы комментария */}
                <div
                    className={styles.itemHead}
                    data-tip={conflict.id === selectedId ? "Ещё раз — открыть карточку" : "Показать на графике"}
                >
                    <span className={styles.who}>
                        {conflict.employerName || (conflict.link ? conflict.link.to : "Сотрудник")}
                    </span>
                    <span className={styles.when}>{formatRange(conflict.from, conflict.to)}</span>
                </div>

                <div className={styles.projects}>
                    {projectsOf.map((project) => (
                        <span
                            key={project.id}
                            className={styles.chip}
                            style={{ backgroundColor: project.paint.chipBackground }}
                            data-tip={`${project.fullInventoryNumber} ${project.name}`}
                        >
                            <span className={project.paint.chipDark ? styles.chipDark : styles.chipLight}>
                                {project.fullInventoryNumber}
                            </span>
                        </span>
                    ))}
                    {section && <span className={styles.section}>{section.code || section.name}</span>}
                    {conflict.deadline && (
                        <span className={styles.deadline}>
                            {conflict.deadline.kind === "plan"
                                ? "плановый срок проекта"
                                : conflict.deadline.kind === "contract"
                                  ? "срок по договору"
                                  : "срок раздела"}
                        </span>
                    )}
                </div>

                <div className={styles.meta}>
                    {details.join(" · ")}
                    {gips.length > 0 && ` · ГИП ${gips.join(", ")}`}
                </div>

                {conflict.mark && (
                    <div className={styles.mark}>
                        <div className={styles.markComment}>{conflict.mark.comment}</div>
                        <div className={styles.markMeta}>
                            {conflict.mark.createdBy}
                            {/* дата приходит уже строкой «24.09.2026» — общий формат дат API */}
                            {conflict.mark.created && ` · ${conflict.mark.created}`}
                        </div>
                    </div>
                )}

                {conflict.markable && canMark(conflict) && (
                    // клики по форме не должны прокручивать график
                    <div className={styles.actions} onClick={(event) => event.stopPropagation()}>
                        {editing === conflict.id ? (
                            <>
                                <div className={`${Classes.INPUT_GROUP} planr-default-input`}>
                                    <TextArea
                                        autoFocus={true}
                                        value={comment}
                                        autoResize={true}
                                        className={Classes.FILL}
                                        placeholder="Почему это допустимо"
                                        maxLength={500}
                                        onChange={(event) => setComment(event.target.value)}
                                    />
                                </div>
                                <div className={styles.buttons}>
                                    <PlanrButton
                                        type="graybtn"
                                        size="small"
                                        className={styles.compact}
                                        disabled={busy}
                                        onClick={() => setEditing(null)}
                                    >
                                        Отмена
                                    </PlanrButton>
                                    <PlanrButton
                                        type="greenish"
                                        size="small"
                                        className={styles.compact}
                                        disabled={busy || !comment.trim()}
                                        onClick={() => submitMark(conflict)}
                                    >
                                        Допустить
                                    </PlanrButton>
                                </div>
                            </>
                        ) : conflict.mark ? (
                            <button className={styles.link} disabled={busy} onClick={() => unmark(conflict)}>
                                Снять пометку
                            </button>
                        ) : (
                            <PlanrButton
                                type="greenish"
                                size="small"
                                className={styles.compact}
                                disabled={busy}
                                onClick={() => startMark(conflict)}
                                data-tip="Пометить конфликт допустимым"
                                data-tip-note="Нужен комментарий. Конфликт уйдёт в группу «Допустимые», комментарий — в историю проекта"
                            >
                                Допустить
                            </PlanrButton>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={styles.panel}>
            <div className={styles.header}>
                <span className={styles.caption}>
                    Конфликты плана{open.length > 0 && <span className={styles.count}>{open.length}</span>}
                </span>
                <GeneralIcon type="general-cross-big" onClick={onClose} className={styles.close} title="Закрыть" />
            </div>

            <div className={styles.body}>
                {!open.length && <div className={styles.empty}>Непогашенных конфликтов нет</div>}

                {KINDS.map((kind) => {
                    const items = open.filter((conflict) => conflict.kind === kind);

                    return items.length ? (
                        <section key={kind} className={classnames(styles.group, KIND_CLASSES[kind])}>
                            {/* заголовок группы липнет к верху списка: видно, в какой категории ты сейчас */}
                            <div className={styles.groupTitle} data-tip={KIND_NOTES[kind]}>
                                <span className={styles.groupMarker} />
                                <span className={styles.groupName}>{CONFLICT_KIND_LABELS[kind]}</span>
                                <span className={styles.groupCount}>{items.length}</span>
                            </div>
                            <div className={styles.groupItems}>{items.map(renderItem)}</div>
                        </section>
                    ) : null;
                })}
            </div>

            {/* «Допустимые» закреплены внизу панели: видны всегда, сколько бы ни было конфликтов выше */}
            <div className={classnames(styles.marked, { [styles.markedOpen]: showMarked && marked.length > 0 })}>
                <div
                    className={classnames(styles.markedTitle, { [styles.markedEmpty]: !marked.length })}
                    onClick={() => marked.length && setShowMarked(!showMarked)}
                    data-tip="Конфликты, помеченные допустимыми с комментарием"
                >
                    <span className={styles.markedCaret}>{showMarked && marked.length ? "▾" : "▸"}</span>
                    <span className={styles.groupName}>Допустимые</span>
                    <span className={styles.markedCount}>{marked.length}</span>
                </div>
                {showMarked && marked.length > 0 && <div className={styles.markedList}>{marked.map(renderItem)}</div>}
            </div>
        </div>
    );
};
